import React, { useState } from "react";
import {
  FaFilter,
  FaSearch,
  FaChevronRight,
  FaChevronLeft,
  FaPlus,
} from "react-icons/fa";
import { BsChevronDown, BsChevronUp } from "react-icons/bs";
import { Link } from "react-router-dom";
import { getClinicalSites } from "../../services/clinical_sites";
import {
  getAgeRange,
  getClinicalSite,
  getClinicalSiteCities,
  getClinicalSiteCountries,
  getClinicalSiteType,
  getLocations,
  getStatus,
} from "../../utils/data";
import moment from "moment";
import Footer from "../../components/footer";
import NewClinicalSite from "../../components/Admin/NewClinicalSite";
import { GetClinicalSitesByOrg } from "../../hooks/getClinicalSitesByOrg";

const ClinicalSites = () => {
  const [clinicalSite, setclinicalSite] = useState(null);
  const [locations, setLocations] = useState(null);
  const [ageRange, setAgeRange] = useState(null);
  const [status, setStatus] = useState(null);
  const [clinicalSiteIcon, setClinicalSiteIcon] = useState(false);
  const [locationsIcon, setLocationsIcon] = useState(false);
  const [ageRangeIcon, setAgeRangeIcon] = useState(false);
  const [statusIcon, setStatusIcon] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Search state
  const [searchTerm, setSearchTerm] = useState("");

  const { data: clinicalSites, isLoading, error } = GetClinicalSitesByOrg();

  const clinicalIconRotate = clinicalSiteIcon ? "rotate(180deg)" : "rotate(0)";
  const locationsIconRotate = locationsIcon ? "rotate(180deg)" : "rotate(0)";
  const ageRangeIconRotate = ageRangeIcon ? "rotate(180deg)" : "rotate(0)";
  const statusIconRotate = statusIcon ? "rotate(180deg)" : "rotate(0)";

  const handleClinical = (selected) => {
    setclinicalSite(selected);
  };

  const handleLocations = (selected) => {
    setLocations(selected);
  };

  const handleAge = (selected) => {
    setAgeRange(selected);
  };

  const handleStatus = (selected) => {
    setStatus(selected);
  };

  // Search handlers
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when search changes
  };

  const clearSearch = () => {
    setSearchTerm("");
    setCurrentPage(1);
  };

  // Filter data based on search term
  const getFilteredData = () => {
    const baseData = clinicalSites?.data || [];

    if (!searchTerm.trim()) {
      return baseData;
    }

    const searchLower = searchTerm.toLowerCase().trim();

    const filtered = baseData.filter((item) => {
      // Search in clinical site name
      const nameMatch = item.name?.toLowerCase().includes(searchLower);

      // Search in clinical site type
      const typeMatch = item.clinicalSiteType
        ?.toLowerCase()
        .includes(searchLower);

      // Search in address
      const addressMatch = `${item.streetAddress || ""} ${item.city || ""} ${
        item.suite || ""
      } ${item.country || ""}`
        .toLowerCase()
        .includes(searchLower);

      // Search in phone number
      const phoneMatch = item.phoneNumber?.toLowerCase().includes(searchLower);

      // Search in date created
      const dateMatch = moment(item.createdAt)
        .format("D MMM YYYY")
        .toLowerCase()
        .includes(searchLower);

      return nameMatch || typeMatch || addressMatch || phoneMatch || dateMatch;
    });

    return filtered;
  };

  // Pagination logic
  const filteredData = getFilteredData();
  const totalElements = filteredData.length;
  const totalPages = Math.ceil(totalElements / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentPageData = filteredData.slice(startIndex, endIndex);

  // Pagination handlers
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Calculate pagination info
  const hasNextPage = currentPage < totalPages;
  const hasPreviousPage = currentPage > 1;

  const formatDate = (date) => {
    return moment(date).format("DD MMM YYYY");
  };

  return (
    <>
      <div className="container-fluid pt-12 pl-21 pb-12 pr-24 h-100 noScroll">
        <div className="card border-0 h-100">
          <div className="card-body mb-3 d-flex flex-column h-100">
            <div className="flex-shrink-0">
              <div className="d-flex justify-content-between">
                <h5 className="font-weight-lighter fs-18">
                  Admin: Manage Clinical Sites & Positions
                </h5>
                <button
                  className="btn btn-primary small-text d-flex align-items-center"
                  data-toggle="modal"
                  data-target="#newClinicalSite"
                >
                  <FaPlus size={13} className="mr-2" />
                  Create New Clinical Site
                </button>
                <NewClinicalSite />
              </div>
              <hr />

              <div className="d-flex justify-content-between align-items-center flex-wrap">
                <div>
                  <h5 className="font-weight-lighter mt-2 fs-18">
                    Master Clinical Sites List
                    {searchTerm.trim() && (
                      <>
                        <br />
                        <small className="text_primary">
                          Search results: {filteredData.length} of{" "}
                          {clinicalSites?.data?.length || 0} sites
                        </small>
                      </>
                    )}
                  </h5>
                  <h6 className="font-weight-lighter mt-2 fs-15">
                    Healthcare Organization
                  </h6>
                </div>
                <div
                  className="d-flex align-items-center"
                  style={{ width: 355 }}
                >
                  <div className="input-group bg_muted rounded">
                    <div className="input-group-prepend">
                      <span
                        className="input-group-text border-0 bg-transparent text-muted pr-3"
                        id="basic-addon1"
                      >
                        <FaSearch size={16} color="#c2c2c2" />
                      </span>
                    </div>
                    <input
                      type="text"
                      className="form-control border-none bg_muted f-13 text-black outline-none rounded mr-3"
                      placeholder="Search by name, type, address, phone, or date..."
                      aria-label="Search"
                      aria-describedby="basic-addon1"
                      value={searchTerm}
                      onChange={handleSearchChange}
                    />
                  </div>
                  <button
                    className="btn btn-outline-primary w-50 ml-3 rounded py-1"
                    data-toggle="modal"
                    data-target="#filterModal"
                  >
                    <FaFilter size={16} />
                    &nbsp;&nbsp; <small>Filter</small>
                  </button>
                </div>
              </div>
            </div>

            {/* Flexible Table Area */}
            <div
              className="flex-grow-1 d-flex flex-column mt-3"
              style={{ minHeight: 0 }}
            >
              <div
                className="flex-grow-1 hide-scrollbar"
                style={{
                  overflowY: "auto",
                  scrollbarWidth: "none", // Firefox
                  msOverflowStyle: "none", // IE and Edge
                }}
              >
                <table className="table table-striped table-bordered mb-0 text-center">
                  <thead
                    className="head_light text-white"
                    style={{ position: "sticky", top: 0, zIndex: 10 }}
                  >
                    <tr className="f-11">
                      <th scope="col">Clinical Site Name</th>
                      <th scope="col">Clinical Site Type</th>
                      <th scope="col">Address</th>
                      <th scope="col">Phone Number</th>
                      <th scope="col">Date Created</th>
                      <th scope="col">HCP</th>
                      <th scope="col">Patients</th>
                      <th scope="col">Go to Clinical Site Page</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!isLoading && currentPageData.length > 0 ? (
                      currentPageData.map((item, i) => (
                        <tr key={i}>
                          <th>
                            <small>{item.name}</small>
                          </th>
                          <td>
                            <small>{item.clinicalSiteType}</small>
                          </td>
                          <td>
                            <small>
                              {item.streetAddress} {item.city} {item.suite}{" "}
                              {item.country}
                            </small>
                          </td>
                          <td>
                            <small>{item.phoneNumber}</small>
                          </td>
                          <td>
                            <small>{formatDate(item.createdAt)}</small>
                          </td>
                          <td>
                            <small>{item.hcp}</small>
                          </td>
                          <td>
                            <small>{item.patients}</small>
                          </td>
                          <td>
                            <Link
                              to={`/admin/clinical_site/details/${item.id}`}
                              onClick={() =>
                                localStorage.setItem("clinical_site", item.id)
                              }
                              className="border_success rounded px-4"
                            >
                              <i className="fa fa-chevron-right" />
                            </Link>
                          </td>
                        </tr>
                      ))
                    ) : !isLoading ? (
                      <tr>
                        <td colSpan="8" className="text-center py-4">
                          <small className="text-muted">
                            {searchTerm.trim()
                              ? `No clinical sites found matching "${searchTerm}"`
                              : "No clinical sites found"}
                          </small>
                        </td>
                      </tr>
                    ) : null}
                    {isLoading &&
                      // Loading skeleton rows
                      [...Array(8)].map((_, index) => (
                        <tr key={`skeleton-${index}`}>
                          <th>
                            <small>Loading...</small>
                          </th>
                          <td>
                            <small>Loading...</small>
                          </td>
                          <td>
                            <small>Loading...</small>
                          </td>
                          <td>
                            <small>Loading...</small>
                          </td>
                          <td>
                            <small>Loading...</small>
                          </td>
                          <td>
                            <small>Loading...</small>
                          </td>
                          <td>
                            <small>Loading...</small>
                          </td>
                          <td>
                            <small>Loading...</small>
                          </td>
                        </tr>
                      ))}
                    {!isLoading &&
                      (!clinicalSites?.data ||
                        clinicalSites.data.length === 0) && (
                        <tr>
                          <td colSpan="8" className="text-center py-4">
                            <small className="text-muted">
                              No clinical sites found
                            </small>
                          </td>
                        </tr>
                      )}
                  </tbody>
                </table>
              </div>

              {/* Fixed Pagination Controls - Bottom of card */}
              {!isLoading &&
                clinicalSites?.data &&
                clinicalSites.data.length > 0 && (
                  <div className="flex-shrink-0 d-flex justify-content-between align-items-center mt-4">
                    {/* Page Size Dropdown - Bottom Left */}
                    <div className="d-flex align-items-center">
                      <span
                        className="text-dark mr-2"
                        style={{ fontSize: "12px", fontWeight: "500" }}
                      >
                        Show
                      </span>
                      <select
                        className="border rounded"
                        style={{
                          width: "50px",
                          height: "24px",
                          fontSize: "12px",
                          fontWeight: "500",
                          color: "#495057",
                          backgroundColor: "#fff",
                          border: "1px solid #ced4da",
                          borderRadius: "6px",
                          padding: "2px 4px",
                          outline: "none",
                        }}
                        value={pageSize}
                        onChange={(e) =>
                          handlePageSizeChange(parseInt(e.target.value))
                        }
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={15}>15</option>
                        <option value={20}>20</option>
                        <option value={25}>25</option>
                      </select>
                      <span
                        className="text-dark ml-2"
                        style={{ fontSize: "12px", fontWeight: "500" }}
                      >
                        entries
                      </span>
                    </div>

                    {/* Pagination Navigation - Bottom Right */}
                    <div className="d-flex align-items-center">
                      <span
                        className="text-muted mr-4"
                        style={{ fontSize: "12px" }}
                      >
                        {totalElements > 0
                          ? `Showing ${Math.min(
                              (currentPage - 1) * pageSize + 1,
                              totalElements
                            )} to ${Math.min(
                              currentPage * pageSize,
                              totalElements
                            )} of ${totalElements} entries`
                          : "No entries to show"}
                      </span>

                      {totalPages > 1 && (
                        <nav aria-label="Page navigation">
                          <div className="d-flex align-items-center">
                            {/* Previous Button */}
                            <button
                              className="btn d-flex align-items-center justify-content-center mr-2"
                              style={{
                                width: "24px",
                                height: "24px",
                                border: "1px solid #ced4da",
                                borderRadius: "6px",
                                backgroundColor: !hasPreviousPage
                                  ? "#d7d7d7"
                                  : "#fff",
                                color: !hasPreviousPage
                                  ? "rgba(83, 89, 107, 0.5)"
                                  : "#495057",
                                cursor: !hasPreviousPage
                                  ? "not-allowed"
                                  : "pointer",
                                paddingLeft: "7px",
                                paddingRight: "7px",
                              }}
                              onClick={handlePreviousPage}
                              disabled={!hasPreviousPage}
                            >
                              <FaChevronLeft size={12} />
                            </button>

                            {/* Page Numbers */}
                            <div className="d-flex">
                              {Array.from(
                                { length: totalPages },
                                (_, i) => i + 1
                              ).map((pageNum) => (
                                <button
                                  key={pageNum}
                                  className="btn d-flex align-items-center justify-content-center mx-1"
                                  style={{
                                    width: "24px",
                                    height: "24px",
                                    border:
                                      currentPage === pageNum
                                        ? "1px solid #53596b"
                                        : "1px solid #ced4da",
                                    borderRadius: "6px",
                                    backgroundColor:
                                      currentPage === pageNum
                                        ? "#53596b"
                                        : "#fff",
                                    color:
                                      currentPage === pageNum
                                        ? "#fff"
                                        : "#495057",
                                    fontSize: "12px",
                                    fontWeight: "500",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => handlePageChange(pageNum)}
                                >
                                  {pageNum}
                                </button>
                              ))}
                            </div>

                            {/* Next Button */}
                            <button
                              className="btn d-flex align-items-center justify-content-center ml-2"
                              style={{
                                width: "24px",
                                height: "24px",
                                border: "1px solid #ced4da",
                                borderRadius: "6px",
                                backgroundColor: !hasNextPage
                                  ? "#d7d7d7"
                                  : "#fff",
                                color: !hasNextPage
                                  ? "rgba(83, 89, 107, 0.5)"
                                  : "#495057",
                                cursor: !hasNextPage
                                  ? "not-allowed"
                                  : "pointer",
                                paddingLeft: "7px",
                                paddingRight: "7px",
                              }}
                              onClick={handleNextPage}
                              disabled={!hasNextPage}
                            >
                              <FaChevronRight size={12} />
                            </button>
                          </div>
                        </nav>
                      )}
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
      <div
        className="modal fade"
        id="filterModal"
        tabindex="-1"
        aria-labelledby="exampleModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog" style={{ width: "395px" }}>
          <div
            className="modal-content pt-3 border-0"
            style={{ borderRadius: "10px" }}
          >
            <div className="modal-header border-0">
              <div className="flex-grow-1 text-center">
                <h5 className="text_primary" style={{ fontWeight: 700 }}>
                  Search Filter
                </h5>
              </div>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <span
                  className="text_primary"
                  style={{ fontSize: 33, fontWeight: "600" }}
                  aria-hidden="true"
                >
                  &times;
                </span>
              </button>
            </div>
            <div className="modal-body px-3">
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Enter Clinical Site Name"
                  className="border-default rounded-sm overlay-dropdown-input small-text pl-12"
                />
              </div>
              <div className="dropdown mb-4">
                <div className="dropdown">
                  <button
                    className="btn btn-block border text-left overlay-dropdown-input"
                    type="button"
                    id="dropdownMenuButton"
                    data-toggle="dropdown"
                    data-display="static"
                    aria-haspopup="true"
                    aria-expanded="false"
                    onClickCapture={() =>
                      setClinicalSiteIcon(!clinicalSiteIcon)
                    }
                  >
                    <div className="d-flex justify-content-between">
                      <small className="small-text">
                        Choose Clinical Site Type
                      </small>
                      <BsChevronDown
                        style={{
                          transform: clinicalIconRotate,
                          transition: "all 0.3s linear",
                        }}
                      />
                    </div>
                  </button>
                  <div
                    className="dropdown-menu dropdown-menu--animate custom border-0 shadow overlay-dropdown-container"
                    aria-labelledby="dropdownMenuButton"
                  >
                    <p className="pl-2 mb-0 pb-2">
                      <small className="small-text">
                        Choose as many as applicable:
                      </small>
                    </p>
                    {getClinicalSiteType.map((item, key) => (
                      <div
                        className="dropdown-item noFilterHover form-group mb-0 form-check ml-4 wa nohover h-38"
                        key={key}
                      >
                        <input
                          type="checkbox"
                          className="form-check-input mt-2"
                          id="exampleCheck1"
                          onChange={() => handleClinical(item.value)}
                        />
                        <label className="form-check-label" for="exampleCheck1">
                          <small className="small-text">{item.label}</small>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="dropdown mb-4">
                <div className="dropdown">
                  <button
                    className="btn btn-block border text-left overlay-dropdown-input"
                    type="button"
                    id="dropdownMenuButton"
                    data-toggle="dropdown"
                    data-display="static"
                    aria-haspopup="true"
                    aria-expanded="false"
                    onClickCapture={() => setLocationsIcon(!locationsIcon)}
                  >
                    <div className="d-flex justify-content-between">
                      <small className="small-text">Choose City</small>
                      <BsChevronDown
                        style={{
                          transform: locationsIconRotate,
                          transition: "all 0.3s linear",
                        }}
                      />
                    </div>
                  </button>
                  <div
                    className="dropdown-menu dropdown-menu--animate custom border-0 shadow overlay-dropdown-container"
                    aria-labelledby="dropdownMenuButton"
                  >
                    <p className="pl-2 mb-0 pb-2">
                      <small className="small-text">
                        Choose as many as applicable:
                      </small>
                    </p>
                    {getClinicalSiteCities.map((item, key) => (
                      <div
                        className="dropdown-item noFilterHover form-group form-check ml-4 wa nohover mb-0 h-38"
                        key={key}
                      >
                        <input
                          type="checkbox"
                          className="form-check-input mt-2"
                          id="exampleCheck1"
                          onChange={() => handleLocations(item.value)}
                        />
                        <label className="form-check-label" for="exampleCheck1">
                          <small className="small-text">{item.label}</small>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="dropdown mb-4">
                <div className="dropdown">
                  <button
                    className="btn btn-block border text-left overlay-dropdown-input"
                    type="button"
                    id="dropdownMenuButton"
                    data-toggle="dropdown"
                    data-display="static"
                    aria-haspopup="true"
                    aria-expanded="false"
                    onClickCapture={() => setAgeRangeIcon(!ageRangeIcon)}
                  >
                    <div className="d-flex justify-content-between">
                      <small className="small-text">Choose Country</small>
                      <BsChevronDown
                        style={{
                          transform: ageRangeIconRotate,
                          transition: "all 0.3s linear",
                        }}
                      />
                    </div>
                  </button>
                  <div
                    className="dropdown-menu dropdown-menu--animate custom border-0 shadow overlay-dropdown-container"
                    aria-labelledby="dropdownMenuButton"
                  >
                    <p className="pl-2 mb-0 pb-2">
                      <small className="small-text">
                        Choose as many as applicable:
                      </small>
                    </p>
                    {getClinicalSiteCountries.map((item, key) => (
                      <div
                        className="dropdown-item noFilterHover form-group form-check ml-4 wa nohover mb-0 h-38"
                        key={key}
                      >
                        <input
                          type="checkbox"
                          className="form-check-input mt-2"
                          id="exampleCheck1"
                          onChange={() => handleAge(item.value)}
                        />
                        <label className="form-check-label" for="exampleCheck1">
                          <small className="small-text">{item.label}</small>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer d-flex justify-content-between align-items-center border-0">
              <button
                type="button"
                className="btn btn_outline_danger overlay-btn"
                data-dismiss="modal"
              >
                CLEAR
              </button>
              <button
                type="button"
                className="btn btn-outline-primary overlay-btn"
              >
                SEARCH
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ClinicalSites;
